import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IconButton, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ROUTES from '@src/constants/route';
import { LANGUAGE } from '@src/constants';
import useTopPaidEvent from '@src/hooks/useTopPaidEvent';
import { COLOR } from '@src/styles/color';
import theme from '@src/styles/theme';
import {
  getDataSessionStorage,
  setDataSessionStorage,
} from '@src/utils/sessionStorage';
import {
  EVENT_HEADER_BUTTON_ATTR,
  EVENT_WIDGET_DOCKED,
} from './EventHeaderButton';

const FLY_DURATION_MS = 450;

const StyledEventWidget = styled('div')({
  position: 'fixed',
  right: 20,
  bottom: 144,
  zIndex: 1000,
  width: 200,
  height: 200,

  '.widget-image': {
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    border: 'none',
    padding: 0,
    background: 'none',
  },
  '.widget-image img': { width: '100%', height: '100%' },
  '.close-button': {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 32,
    height: 32,
    color: COLOR.white,
    border: `1px solid ${COLOR.white48}`,
    backgroundColor: COLOR.indigo[32],
    '&:hover': { backgroundColor: COLOR.indigo[48] },
  },
});

// Floating promo for the event on every desktop screen; closing it lasts for the tab and that account only.
const EventWidget = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { config, isRunning } = useTopPaidEvent();
  const userId = useSelector((state) => state.auth.user?.id);

  const closedKey =
    config && userId && `${config.eventId}_widget_closed_${userId}`;
  // The key closed in this render tree; storage is re-read so switching accounts picks up that account's choice.
  const [closedFor, setClosedFor] = useState(null);
  const [leavingStyle, setLeavingStyle] = useState(null);
  // While flying out the key is already saved, so storage is skipped until the animation ends.
  const closed =
    Boolean(closedKey) &&
    (closedFor === closedKey ||
      (!leavingStyle && Boolean(getDataSessionStorage(closedKey))));
  const widgetRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    setLeavingStyle(null);
    return () => clearTimeout(closeTimerRef.current);
  }, [closedKey]);

  if (
    !isRunning ||
    !config.bannerEnabled ||
    !closedKey ||
    closed ||
    isMobile ||
    location.pathname === ROUTES.TOP_PAID_EVENT
  )
    return null;

  const finishClose = (docked) => {
    setClosedFor(closedKey);
    if (docked) window.dispatchEvent(new Event(EVENT_WIDGET_DOCKED));
  };

  // Flies the widget into the header button so users see where the event went; fades out where there is none.
  const handleClose = () => {
    if (leavingStyle) return;
    setDataSessionStorage(closedKey, '1');
    const button = document.querySelector(`[${EVENT_HEADER_BUTTON_ATTR}]`);
    // A hidden header button has an empty box; flying there would land in the corner.
    const target = button?.getBoundingClientRect().width ? button : null;
    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (reduceMotion || !widgetRef.current) {
      finishClose(Boolean(target));
      return;
    }

    const from = widgetRef.current.getBoundingClientRect();
    const transition = `transform ${FLY_DURATION_MS}ms ease-in, opacity ${FLY_DURATION_MS}ms ease-in`;
    if (target) {
      const to = target.getBoundingClientRect();
      const dx = to.left + to.width / 2 - (from.left + from.width / 2);
      const dy = to.top + to.height / 2 - (from.top + from.height / 2);
      const scale = to.width / from.width;
      setLeavingStyle({
        transition,
        transform: `translate(${dx}px, ${dy}px) scale(${scale})`,
        opacity: 0.4,
        pointerEvents: 'none',
      });
    } else {
      setLeavingStyle({ transition, opacity: 0, pointerEvents: 'none' });
    }
    closeTimerRef.current = setTimeout(
      () => finishClose(Boolean(target)),
      FLY_DURATION_MS,
    );
  };

  return (
    <StyledEventWidget ref={widgetRef} style={leavingStyle || undefined}>
      <button
        type="button"
        className="widget-image"
        onClick={() => history.push(ROUTES.TOP_PAID_EVENT)}
      >
        <img
          src={
            i18n.language === LANGUAGE.VN
              ? config.images.widgetBannerVi
              : config.images.widgetBannerEn
          }
          alt={t('topPaidEventWidgetAlt')}
        />
      </button>
      <IconButton
        className="close-button"
        aria-label={t('topPaidEventCloseWidget')}
        onClick={handleClose}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </StyledEventWidget>
  );
};

export default EventWidget;
